/* Two small SVG charts over the same bucketed window, sharing one x axis so a failure spike lines up with its
   latency. Bars and lines stretch to the container width (viewBox + preserveAspectRatio="none"); labels are HTML so
   text never stretches. Hovering a bucket shows its numbers via the SVG <title>. */
import type { inferRouterOutputs } from "@trpc/server"

import type { AppRouter } from "../../../../aixinternal-api/src/router/_app"

export type TUptimeBucket = inferRouterOutputs<AppRouter>["uptimeMonitors"]["getDetail"]["buckets"][number]

const VIEW_W = 1000
const BAR_H = 120
const LINE_H = 140
const BAR_GAP_RATIO = 0.18
/** A bucket with no failures still gets a sliver so "checked and fine" reads differently from "no data" */
const OK_SLIVER_PX = 3
const SECOND_MS = 1000
/** At or above this share of failed checks a bucket reads as an outage, below it as flapping */
const OUTAGE_SHARE = 0.5

/** Theme tokens only (light + dark): green = checked and fine, orange = some failed, red = mostly failed */
const barFill = (failed: number, share: number) => {
  if (failed === 0) return "hsl(var(--chart-3) / 0.5)"
  return share >= OUTAGE_SHARE ? "hsl(var(--destructive))" : "hsl(var(--chart-4))"
}

const formatMs = (ms: number | null) => {
  if (ms === null) return "—"
  return ms < SECOND_MS ? `${Math.round(ms)}ms` : `${(ms / SECOND_MS).toFixed(1)}s`
}

const bucketLabel = (bucket: TUptimeBucket) =>
  new Date(bucket.bucket).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })

function AxisLabels({ buckets }: { buckets: TUptimeBucket[] }) {
  const first = buckets[0]
  const last = buckets[buckets.length - 1]
  if (!first || !last) return null
  return (
    <div className="flex justify-between text-[11px] text-muted-foreground tabular-nums">
      <span>{bucketLabel(first)}</span>
      <span>{bucketLabel(last)}</span>
    </div>
  )
}

/** Failed checks per bucket. Height = failed count against the worst bucket; colour = share of checks that failed. */
export function FailureChart({ buckets }: { buckets: TUptimeBucket[] }) {
  const maxFailed = Math.max(1, ...buckets.map((b) => b.failed))
  const slot = VIEW_W / Math.max(1, buckets.length)
  const barW = slot * (1 - BAR_GAP_RATIO)
  const failedBuckets = buckets.filter((b) => b.failed > 0).length

  return (
    <figure className="m-0 flex flex-col gap-1">
      <figcaption className="flex items-baseline justify-between text-sm">
        <span className="font-medium">Failed checks</span>
        <span className="text-xs text-muted-foreground">
          {failedBuckets
            ? `${failedBuckets} of ${buckets.length} intervals had failures`
            : "No failures in this window"}
        </span>
      </figcaption>
      <svg
        viewBox={`0 0 ${VIEW_W} ${BAR_H}`}
        preserveAspectRatio="none"
        className="h-[120px] w-full"
        role="img"
        aria-label="Failed checks per interval"
      >
        {buckets.map((b, i) => {
          const x = i * slot + (slot - barW) / 2
          if (b.total === 0)
            return (
              <rect key={i} x={x} y={BAR_H - 1} width={barW} height={1} fill="hsl(var(--border))">
                <title>{`${bucketLabel(b)} · no checks`}</title>
              </rect>
            )
          const share = b.failed / b.total
          const h = b.failed ? Math.max(OK_SLIVER_PX, (b.failed / maxFailed) * (BAR_H - 4)) : OK_SLIVER_PX
          const fill = barFill(b.failed, share)
          return (
            <rect key={i} x={x} y={BAR_H - h} width={barW} height={h} rx={1} fill={fill}>
              <title>{`${bucketLabel(b)} · ${b.failed} of ${b.total} failed · p50 ${formatMs(b.p50)}`}</title>
            </rect>
          )
        })}
      </svg>
      <AxisLabels buckets={buckets} />
    </figure>
  )
}

/** p50 latency line with the bucket max as a faint band; a dashed rule marks the monitor's timeout when in range. */
export function LatencyChart({ buckets, timeoutMs }: { buckets: TUptimeBucket[]; timeoutMs: number }) {
  const points = buckets.map((b, i) => ({ i, b }))
  const withData = points.filter(({ b }) => b.p50 !== null)
  const top = Math.max(1, ...withData.map(({ b }) => b.max ?? b.p50 ?? 0))
  const slot = VIEW_W / Math.max(1, buckets.length)
  const x = (i: number) => i * slot + slot / 2
  const y = (ms: number) => LINE_H - (ms / top) * (LINE_H - 6)

  // Break the line across empty buckets instead of drawing a straight bridge over missing data
  const segments: string[] = []
  let current = ""
  for (const { i, b } of points) {
    if (b.p50 === null) {
      if (current) segments.push(current)
      current = ""
      continue
    }
    current += `${current ? "L" : "M"}${x(i).toFixed(1)},${y(b.p50).toFixed(1)}`
  }
  if (current) segments.push(current)

  return (
    <figure className="m-0 flex flex-col gap-1">
      <figcaption className="flex items-baseline justify-between text-sm">
        <span className="font-medium">Latency</span>
        <span className="text-xs text-muted-foreground">p50 line · max band · peak {formatMs(top)}</span>
      </figcaption>
      <svg
        viewBox={`0 0 ${VIEW_W} ${LINE_H}`}
        preserveAspectRatio="none"
        className="h-[140px] w-full"
        role="img"
        aria-label="Latency per interval"
      >
        {withData.map(({ i, b }) => (
          <rect
            key={i}
            x={x(i) - slot / 2}
            y={y(b.max ?? b.p50 ?? 0)}
            width={slot}
            height={LINE_H - y(b.max ?? b.p50 ?? 0)}
            fill="hsl(var(--primary) / 0.08)"
          >
            <title>{`${bucketLabel(b)} · p50 ${formatMs(b.p50)} · max ${formatMs(b.max)}`}</title>
          </rect>
        ))}
        {timeoutMs <= top && (
          <line
            x1={0}
            x2={VIEW_W}
            y1={y(timeoutMs)}
            y2={y(timeoutMs)}
            stroke="hsl(var(--destructive))"
            strokeDasharray="6 6"
            vectorEffect="non-scaling-stroke"
          />
        )}
        {segments.map((d) => (
          <path
            key={d}
            d={d}
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>
      <AxisLabels buckets={buckets} />
    </figure>
  )
}
